import type { AppState, FieldDescriptor, VectorLayerModel } from '../core/types';
import { effectiveFieldCatalog, fieldValueForMasterEntity, inferFieldType, isUserVisibleSourceField, masterSourceFieldLabel } from '../domain/attributes';
import { createLayerClassification, createMasterClassification } from '../domain/classification';
import { entityViewEntities, entityViewFragmentIds } from '../domain/entityView';
import { subareaFieldDescriptors } from '../domain/operationalSubareas';
import type { ClassificationConfig, SplitPart } from './config';

export type QuickPaletteKey = 'auto' | 'agro' | 'viridis' | 'spectral';
/** Distinct, colour-blind aware qualitative palette for categories (automatic). */
export const QUALITATIVE_PALETTE = ['#2b7bba', '#e8871e', '#3a9d4f', '#d1403b', '#8a63c4', '#8c5a3c', '#d36bb1', '#6b7a86', '#b4b232', '#25aec3'] as const;
export const SEQUENTIAL_PALETTES: Readonly<Record<Exclude<QuickPaletteKey, 'auto'>, readonly string[]>> = {
  agro: ['#1b4332', '#40916c', '#95d5b2', '#f4a261', '#e76f51'],
  viridis: ['#440154', '#3b528b', '#21918c', '#5ec962', '#fde725'],
  spectral: ['#9e0142', '#f46d43', '#fee08b', '#66c2a5', '#3288bd'],
};

/** Neutral grey for context entities so they read as background, never as a category. */
export const NEUTRAL_CONTEXT = '#9aa5ad';

export interface QuickFieldOption { readonly id: string; readonly label: string; readonly group: 'Excel' | 'Proyecto' | 'Maestro' | 'Calculados'; readonly type: FieldDescriptor['type'] }

const GROUP_ORDER: Readonly<Record<QuickFieldOption['group'], number>> = { Excel: 0, Proyecto: 1, Maestro: 2, Calculados: 3 };
const LEVEL_RANK = { finca: 0, suerte: 1, tablon: 2, fragment: 3, feature: 4, subarea: 4 } as const;

/** Fields a user can classify by at the current "Ver por" level, with human names only. */
export function quickFieldOptions(state: Pick<AppState, 'model' | 'operationalFields' | 'entityViewLevel'>, layer: VectorLayerModel | undefined): readonly QuickFieldOption[] {
  if (!layer) return [];
  if (layer.kind === 'master' && state.model) {
    const level = LEVEL_RANK[state.entityViewLevel];
    return effectiveFieldCatalog(state.model, state.operationalFields)
      .filter((field) => field.level !== 'subarea' && field.level !== 'feature' && LEVEL_RANK[field.level] <= Math.max(level, LEVEL_RANK.fragment) && (field.source !== 'source' || isUserVisibleSourceField(field.name)))
      .filter((field) => !field.id.startsWith('system:logical:fragment') && (field.source !== 'system' || LEVEL_RANK[field.level] <= level))
      .map((field): QuickFieldOption => ({ id: field.id, label: field.source === 'source' ? masterSourceFieldLabel(field.name) : field.name, group: field.source === 'join' ? 'Excel' : field.source === 'project' ? 'Proyecto' : field.source === 'source' ? 'Maestro' : 'Calculados', type: field.type }))
      .sort((a, b) => GROUP_ORDER[a.group] - GROUP_ORDER[b.group]);
  }
  if (layer.kind === 'subarea') return subareaFieldDescriptors(state.operationalFields).map((field) => ({ id: field.id, label: field.name, group: field.source === 'project' ? 'Proyecto' : 'Calculados', type: field.type }));
  return layer.attributeFields.map((name) => ({ id: name, label: name, group: 'Maestro', type: inferFieldType(layer.features.slice(0, 300).map(({ attributes }) => attributes[name] ?? null)) }));
}

const distinctValueCount = (state: Pick<AppState, 'model' | 'operationalFields' | 'entityViewLevel'>, layer: VectorLayerModel, fieldId: string): number => {
  const values = new Set<string>();
  if (layer.kind === 'master' && state.model) {
    for (const entity of entityViewEntities(state.model, state.entityViewLevel)) {
      const value = fieldValueForMasterEntity(state.model, fieldId, entity.fragmentIds, entity.ids, state.operationalFields);
      if (value !== null && value !== undefined && value !== '') values.add(String(value));
      if (values.size > 12) break;
    }
  } else layer.features.forEach(({ attributes }) => { const value = attributes[fieldId]; if (value !== null && value !== undefined && value !== '') values.add(String(value)); });
  return values.size;
};

/**
 * One-step classification ("Clasificar → Campo"): categorical fields get distinct automatic colours;
 * numeric fields with many values become 5 quantile classes. The theme target is the whole layer, so
 * scope, drill-down and filters only change what is visible, never the classification definition.
 */
export function quickClassificationConfig(state: AppState, layer: VectorLayerModel, fieldId: string, paletteKey: QuickPaletteKey, option?: QuickFieldOption, splitPart?: SplitPart): ClassificationConfig {
  const numeric = option?.type === 'number' && distinctValueCount(state, layer, fieldId) > 8;
  const palette = paletteKey === 'auto' ? numeric ? SEQUENTIAL_PALETTES.viridis : QUALITATIVE_PALETTE : SEQUENTIAL_PALETTES[paletteKey];
  const ids = quickThemeTargetIds(state, layer, fieldId);
  const previous = state.classificationConfig;
  const reuse = previous.id.startsWith('quick-') ? previous.id : `quick-${Date.now()}`;
  // V1.8.1: a part of the «Desglosar categorías» table is classified by its own name; the raw field stays the source.
  const split = splitPart && previous.categorySplit?.sourceField === fieldId ? previous.categorySplit : undefined;
  const name = split ? (splitPart === 'primary' ? split.primaryName : split.secondaryName) : option?.label ?? fieldId;
  const { splitPart: _previousPart, ...rest } = previous; void _previousPart;
  const samePart = (previous.splitPart ?? null) === (split ? splitPart! : null);
  const base: ClassificationConfig = {
    ...rest,
    ...(split ? { splitPart } : {}),
    id: reuse,
    name,
    layerId: layer.id,
    field: fieldId,
    mode: numeric ? 'graduated' : 'categorized',
    method: 'quantile',
    classCount: 5,
    palette,
    reversePalette: false,
    level: layer.kind === 'master' ? state.entityViewLevel : 'feature',
    themeTarget: { source: ids.size === layer.features.length ? 'layer' : 'custom', scope: state.workScope, featureIds: [...ids] },
    thematicView: previous.field ? previous.thematicView : 'context',
    remainderStyle: previous.field ? previous.remainderStyle : 'dim',
    remainderColor: previous.id.startsWith('quick-') ? previous.remainderColor : NEUTRAL_CONTEXT,
    categories: previous.field === fieldId && samePart && previous.layerId === layer.id && previous.palette === palette ? previous.categories : [],
    // Exceptions belong to one Suerte field: another field or layer starts with them off.
    partialExceptions: previous.field === fieldId && samePart && previous.layerId === layer.id ? previous.partialExceptions : undefined,
    legend: { ...previous.legend, title: name, visible: true },
  };
  return layer.kind === 'master' && state.model ? createMasterClassification(state.model, base, ids, state.operationalFields) : createLayerClassification(layer, base, ids);
}

/**
 * "Datos" of a quick classification: entities that have a record for the field. For Excel/project fields
 * these are the related entities (an empty cell inside the Excel is "Sin valor"); entities absent from the
 * Excel are the context ("resto"). Maestro fields cover every entity.
 */
export function quickThemeTargetIds(state: Pick<AppState, 'model' | 'operationalFields'>, layer: VectorLayerModel, fieldId: string): ReadonlySet<string> {
  const operational = state.operationalFields.find(({ descriptor }) => descriptor.id === fieldId);
  if (layer.kind !== 'master' || !state.model || !operational || operational.descriptor.level === 'subarea' || operational.descriptor.level === 'feature') return new Set(layer.features.map(({ id }) => id));
  const level = operational.descriptor.level;
  return new Set(Object.keys(operational.valuesByEntityId).flatMap((entityId) => entityViewFragmentIds(state.model!, level, entityId)));
}

export const paletteKeyFor = (palette: readonly string[]): QuickPaletteKey => {
  const match = (Object.entries(SEQUENTIAL_PALETTES) as [Exclude<QuickPaletteKey, 'auto'>, readonly string[]][]).find(([, colors]) => colors.join() === palette.join());
  return match?.[0] ?? 'auto';
};
